import React, { useEffect, useState } from 'react'
import axios from 'axios'

const apiUrl = process.env.REACT_APP_API_URL

const formatPrice = (value) => {
  const amount = Number(value || 0)
  return `Rs ${amount.toLocaleString('en-IN')}`
}

const resolvePreviewImage = (path) => {
  if (!path) return '/assets/images/no-product-image.png'
  if (/^https?:\/\//i.test(path)) return path
  return `${apiUrl}/${String(path).replace(/^\/+/, '')}`
}

const computeStock = (product) => {
  if (!product) return 0
  if (Array.isArray(product.skus) && product.skus.length > 0) {
    return product.skus.reduce((sum, s) => sum + (Number(s?.quantity) || 0), 0)
  }
  return Number(product.quantity) || 0
}

const GiftRow = ({
  index,
  row,
  onChange,
  onRemove,
  currentProductId,
  dataChangeHandler,
  disabled,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [preview, setPreview] = useState(row.preview || null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!row.product_id) {
      setPreview(null)
      return
    }
    if (row.preview && String(row.preview._id) === String(row.product_id)) {
      setPreview(row.preview)
      return
    }
    let isMounted = true
    setLoading(true)
    axios
      .get(`${apiUrl}/api/products/edit/${row.product_id}`, { timeout: 8000 })
      .then((res) => {
        if (!isMounted) return
        setPreview(res.data)
        setLoading(false)
      })
      .catch(() => {
        if (!isMounted) return
        setPreview(null)
        setLoading(false)
      })
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.product_id])

  const handleSearchChange = async (event) => {
    const value = event.target.value
    setSearchTerm(value)
    setError('')
    if (!value || value.trim().length < 2) {
      setSearchResults([])
      return
    }
    try {
      const response = await axios.get(
        `${apiUrl}/api/products?keyword=${encodeURIComponent(value)}`,
        { timeout: 8000 }
      )
      setSearchResults(response?.data?.products || [])
    } catch (err) {
      setSearchResults([])
    }
  }

  const handleSelectGift = (product) => {
    if (!product) return
    if (String(product._id) === String(currentProductId)) {
      setError('Free gift cannot be the same product.')
      return
    }
    onChange({ ...row, product_id: product._id, preview: product })
    setPreview(product)
    setSearchTerm('')
    setSearchResults([])
    setError('')
    if (dataChangeHandler) dataChangeHandler()
  }

  const stock = computeStock(preview)

  return (
    <div className='border rounded p-3 mb-3'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <strong>Gift #{index + 1}</strong>
        <button type='button' className='btn btn-sm btn-outline-danger' onClick={onRemove}>
          Remove
        </button>
      </div>

      <div className='mb-2'>
        <label className='form-label'>Search product</label>
        <input
          type='search'
          className='form-control'
          placeholder='Search by name or SKU'
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={disabled}
        />
        {searchResults.length > 0 && !disabled ? (
          <div className='list-group mt-2'>
            {searchResults.slice(0, 6).map((item) => (
              <button
                type='button'
                key={item._id}
                className='list-group-item list-group-item-action'
                onClick={() => handleSelectGift(item)}
              >
                {item.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {error ? <div className='text-danger small mb-2'>{error}</div> : null}

      <div className='row g-2 mb-2'>
        <div className='col-md-4'>
          <label className='form-label'>Qty</label>
          <input
            type='number'
            min='1'
            className='form-control'
            value={row.qty ?? 1}
            onChange={(e) => {
              onChange({ ...row, qty: e.target.value })
              if (dataChangeHandler) dataChangeHandler()
            }}
            disabled={disabled}
          />
        </div>
        <div className='col-md-8'>
          <label className='form-label'>Qty mode</label>
          <select
            className='form-select'
            value={row.qty_mode || 'FIXED'}
            onChange={(e) => {
              onChange({ ...row, qty_mode: e.target.value })
              if (dataChangeHandler) dataChangeHandler()
            }}
            disabled={disabled}
          >
            <option value='FIXED'>Fixed</option>
            <option value='MATCH_TRIGGER_QTY'>Match trigger qty</option>
          </select>
        </div>
      </div>

      <div className='border rounded p-3 bg-light'>
        <h6 className='mb-2'>Preview</h6>
        {loading ? (
          <p className='text-muted small mb-0'>Loading...</p>
        ) : preview ? (
          <div className='d-flex gap-3 align-items-start'>
            <img
              src={resolvePreviewImage(preview?.image?.[0])}
              alt={preview?.name}
              style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px' }}
            />
            <div>
              <div className='fw-semibold'>{preview?.name}</div>
              <div className='small text-muted'>{formatPrice(preview?.price)}</div>
              <div className='small text-muted'>Stock: {stock}</div>
            </div>
          </div>
        ) : (
          <p className='text-muted small mb-0'>Select a gift product to preview.</p>
        )}
      </div>
    </div>
  )
}

const FreeGift = ({
  enabled,
  setEnabled,
  // legacy single-gift props (kept so callers don't break — first row mirrors them)
  giftProductId,
  setGiftProductId,
  giftQty,
  setGiftQty,
  giftQtyMode,
  setGiftQtyMode,
  // new multi-gift props
  freeGifts,
  setFreeGifts,
  currentProductId,
  dataChangeHandler,
}) => {
  const [rows, setRows] = useState([])
  const [hydrated, setHydrated] = useState(false)

  // Hydrate rows from incoming props once we have data — handles async product fetches
  useEffect(() => {
    if (hydrated) return
    if (Array.isArray(freeGifts) && freeGifts.length > 0) {
      setRows(
        freeGifts.map((g) => ({
          product_id: g.product_id || '',
          qty: g.qty ?? 1,
          qty_mode: g.qty_mode || 'FIXED',
        }))
      )
      setHydrated(true)
      return
    }
    if (giftProductId) {
      setRows([{ product_id: giftProductId, qty: giftQty ?? 1, qty_mode: giftQtyMode || 'FIXED' }])
      setHydrated(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeGifts, giftProductId, giftQty, giftQtyMode])

  // Keep legacy single-gift fields and new freeGifts array in sync with rows (only after hydration)
  useEffect(() => {
    if (!hydrated) return
    if (typeof setFreeGifts === 'function') {
      setFreeGifts(
        rows
          .filter((r) => r.product_id)
          .map((r) => ({
            product_id: r.product_id,
            qty: Number(r.qty) || 1,
            qty_mode: r.qty_mode || 'FIXED',
          }))
      )
    }
    const primary = rows[0]
    if (typeof setGiftProductId === 'function') setGiftProductId(primary?.product_id || '')
    if (typeof setGiftQty === 'function') setGiftQty(primary?.qty ?? 1)
    if (typeof setGiftQtyMode === 'function') setGiftQtyMode(primary?.qty_mode || 'FIXED')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, hydrated])

  const updateRow = (index, next) => {
    setRows((prev) => prev.map((r, i) => (i === index ? next : r)))
  }

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index))
    if (dataChangeHandler) dataChangeHandler()
  }

  const addRow = () => {
    setRows((prev) => [...prev, { product_id: '', qty: 1, qty_mode: 'FIXED' }])
    if (dataChangeHandler) dataChangeHandler()
  }

  return (
    <div className='card mb-3'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <label className='form-label mb-0'>Free Gift</label>
        <div className='form-check form-switch m-0'>
          <input
            className='form-check-input'
            type='checkbox'
            id='freeGiftEnabled'
            checked={Boolean(enabled)}
            onChange={(e) => {
              setEnabled(e.target.checked)
              if (dataChangeHandler) dataChangeHandler()
            }}
          />
        </div>
      </div>
      <div className='card-body'>
        {!enabled ? (
          <p className='text-muted small mb-0'>Toggle on to add free gifts to this product.</p>
        ) : (
          <>
            {rows.length === 0 ? (
              <p className='text-muted small'>No gifts added yet.</p>
            ) : (
              rows.map((row, index) => (
                <GiftRow
                  key={index}
                  index={index}
                  row={row}
                  onChange={(next) => updateRow(index, next)}
                  onRemove={() => removeRow(index)}
                  currentProductId={currentProductId}
                  dataChangeHandler={dataChangeHandler}
                  disabled={!enabled}
                />
              ))
            )}
            <button type='button' className='btn btn-outline-primary btn-sm' onClick={addRow}>
              + Add another gift
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default FreeGift
